'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type {
  AiApplicationDto,
  UpdateAiApplicationDto,
} from '@terra/shared-contracts-ai-applications';
import {
  SettingsCard,
  SettingsEditDialog,
  SettingsRow,
  SettingsSection,
} from '@terra/ui-design-system';
import {
  getInterfaceProbeStatus,
  readInterfaceDiscoveryFailureReason,
} from './aiInterfaceMetadata';
import {
  AiApplicationInterfacesEditor,
  mapStoredInterfaceToRow,
  type AiApplicationInterfaceRow,
} from './AiApplicationInterfacesEditor';
import type { AiApplicationInterfaceType } from './AiApplicationProtocolSelector';
import { buildAiApplicationInterfaceUpdate } from './buildAiApplicationInterfaceUpdate';

export type AiApplicationInterfacesSettingsSectionProps = {
  aiApplication:
    | Pick<
        AiApplicationDto,
        'url' | 'interfaceTypes' | 'interfaces' | 'discoveryStatus'
      >
    | undefined;
  isLoading: boolean;
  isSaving?: boolean;
  isReadOnly?: boolean;
  canEditUrl: boolean;
  urlEditRestrictionReason?: string;
  onSave: (dto: UpdateAiApplicationDto) => Promise<boolean>;
};

const toInterfaceRows = (
  interfaces: AiApplicationDto['interfaces'],
): AiApplicationInterfaceRow[] =>
  interfaces.map((item) =>
    mapStoredInterfaceToRow({
      id: item.id,
      name: item.name,
      url: item.url,
      type: item.type,
      metadata: item.metadata,
      probeStatus: getInterfaceProbeStatus(item.metadata),
      discoveryFailureReason: readInterfaceDiscoveryFailureReason(
        item.metadata,
      ),
    }),
  );

export function AiApplicationInterfacesSettingsSection({
  aiApplication,
  isLoading,
  isSaving,
  isReadOnly,
  canEditUrl,
  urlEditRestrictionReason,
  onSave,
}: AiApplicationInterfacesSettingsSectionProps) {
  const tAi = useTranslations('aiApplications');
  const tAiSettings = useTranslations('aiApplications.settings');
  const tCommon = useTranslations('designSystem.common');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [draftBaseUrl, setDraftBaseUrl] = useState('');
  const [draftProtocols, setDraftProtocols] = useState<
    AiApplicationInterfaceType[]
  >(['HTTP']);
  const [draftInterfaceRows, setDraftInterfaceRows] = useState<
    AiApplicationInterfaceRow[]
  >([]);
  const getProtocolLabel = (protocol: AiApplicationInterfaceType) =>
    tAi(`interfaceTypeOptions.${protocol}`);
  const storedInterfaces = aiApplication?.interfaces ?? [];
  const protocolSummary =
    aiApplication?.interfaceTypes
      ?.map((protocol) => getProtocolLabel(protocol))
      .join(', ') ?? tAi('emptyValue');

  const openDialog = () => {
    setDraftBaseUrl(aiApplication?.url ?? '');
    setDraftProtocols(
      aiApplication?.interfaceTypes?.length
        ? [...aiApplication.interfaceTypes]
        : ['HTTP'],
    );
    setDraftInterfaceRows(toInterfaceRows(storedInterfaces));
    setIsDialogOpen(true);
  };

  return (
    <SettingsSection title={tAiSettings('general.interfaces')}>
      <SettingsCard isLoading={isLoading}>
        <SettingsRow
          label={tAiSettings('general.discoveryStatus')}
          value={
            aiApplication?.discoveryStatus
              ? tAiSettings(
                  `general.discoveryStatusValues.${aiApplication.discoveryStatus}`,
                )
              : tAi('emptyValue')
          }
          isReadOnly
        />
        <SettingsRow
          label={tAi('fields.url')}
          value={aiApplication?.url ?? tAi('emptyValue')}
          action={
            canEditUrl
              ? { label: tCommon('edit'), onClick: openDialog }
              : undefined
          }
          tooltip={canEditUrl ? undefined : urlEditRestrictionReason}
          isReadOnly={isReadOnly || !canEditUrl}
        />
        <SettingsRow
          label={tAi('fields.interfaceTypes')}
          value={protocolSummary}
          action={{ label: tCommon('edit'), onClick: openDialog }}
          isReadOnly={isReadOnly}
        />
        {storedInterfaces.map((item) => {
          const probeStatus = getInterfaceProbeStatus(item.metadata);
          const probeLabel = probeStatus
            ? tAiSettings(`general.probeStatus.${probeStatus}`)
            : null;

          return (
            <SettingsRow
              key={item.id}
              label={getProtocolLabel(item.type)}
              value={probeLabel ? `${item.url} (${probeLabel})` : item.url}
              isReadOnly
            />
          );
        })}
      </SettingsCard>

      <SettingsEditDialog
        open={isDialogOpen}
        onOpenChange={(open) => !open && setIsDialogOpen(false)}
        title={tAiSettings('edit.interfaces')}
        cancelText={tCommon('cancel')}
        submitText={tCommon('saveChanges')}
        contentMaxHeight="60vh"
        loading={isSaving}
        isSubmitDisabled={!draftBaseUrl.trim() || draftProtocols.length === 0}
        onSubmit={async () => {
          const saved = await onSave(
            buildAiApplicationInterfaceUpdate({
              baseUrl: draftBaseUrl,
              interfaceTypes: draftProtocols,
              interfaceRows: draftInterfaceRows,
              storedInterfaces,
            }),
          );

          if (saved) {
            setIsDialogOpen(false);
          }
        }}
      >
        <AiApplicationInterfacesEditor
          baseUrl={draftBaseUrl}
          onBaseUrlChange={setDraftBaseUrl}
          selectedProtocols={draftProtocols}
          onSelectedProtocolsChange={setDraftProtocols}
          interfaceRows={draftInterfaceRows}
          onInterfaceRowsChange={setDraftInterfaceRows}
          isBaseUrlDisabled={!canEditUrl}
          showProbeStatus
        />
      </SettingsEditDialog>
    </SettingsSection>
  );
}
